import { useState, useEffect } from "react";
import { Outlet } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
// Inquiry dialog removed; contact now scrolls to contact section
import sbGanesh from "../assets/SBGanpati.JPG";
import { FaMobileAlt } from "react-icons/fa";
import { MdOutlineEmail } from "react-icons/md";
import { FaBars, FaTimes } from "react-icons/fa";

export const Navbar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState("Home");

  const [scrolled, setScrolled] = useState(false);

  const navArray = ["Home", "About", "Services", "Contact"];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleClick = (name) => {
    setSelectedItem(name);
    setSidebarOpen(false);

    const targetElement = document.getElementById(name.toLowerCase());
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  const navbarVariants = {
    top: {
      backgroundColor: "rgba(26, 119, 176, 0.95)",
      backdropFilter: "blur(10px)",
      boxShadow: "0 2px 20px rgba(0,0,0,0.1)",
    },
    scrolled: {
      backgroundColor: "rgba(26, 119, 176, 0.98)",
      backdropFilter: "blur(15px)",
      boxShadow: "0 4px 30px rgba(0,0,0,0.2)",
    },
  };

  const menuItemVariants = {
    hover: {
      scale: 1.05,
      color: "#60A5FA",
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.95 },
  };

  const mobileMenuVariants = {
    hidden: {
      opacity: 0,
      height: 0,
      transition: { duration: 0.3 },
    },
    visible: {
      opacity: 1,
      height: "auto",
      transition: { duration: 0.3 },
    },
  };

  return (
    <>
      <motion.div
        className="w-full fixed top-0 z-50 transition-all duration-300"
        animate={scrolled ? "scrolled" : "top"}
        variants={navbarVariants}
        initial="top"
      >
        {/* Navbar */}
        <div className="flex justify-between items-center h-20 px-6">
          {/* Logo & Contact Details */}
          <div className="flex items-center gap-8">
            <motion.div
              className="flex items-center gap-4"
              whileHover={{ scale: 1.02 }}
            >
              <img
                src={sbGanesh}
                className="h-16 w-16 rounded-full border-2 border-white/20"
                alt="Firm Logo"
              />
              <div className="hidden lg:block">
                <h1 className="text-white font-bold text-lg leading-tight">
                  Shree Ballaleshwar
                  <br />
                  <span className="text-blue-200 text-sm">
                    Industrial Solutions
                  </span>
                </h1>
              </div>
            </motion.div>

            {/* Contact Info - Hidden on smaller screens */}
            <div className="hidden xl:flex items-center gap-6">
              <motion.div
                className="flex items-center gap-2 text-white"
                whileHover={{ scale: 1.05 }}
              >
                <FaMobileAlt className="text-blue-300" />
                <span className="text-sm font-medium">+91 9423376674</span>
              </motion.div>

              <motion.div
                className="flex items-center gap-2 text-white"
                whileHover={{ scale: 1.05 }}
              >
                <MdOutlineEmail className="text-blue-300" />
                <span className="text-sm font-medium">
                  info@sbindustrialsolution.com
                </span>
              </motion.div>
            </div>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            {navArray.map((name, index) => (
              <motion.div
                key={index}
                className={`relative px-4 py-2 cursor-pointer font-medium transition-all duration-200 ${
                  selectedItem === name ? "text-blue-200" : "text-white"
                }`}
                onClick={() => handleClick(name)}
                variants={menuItemVariants}
                whileHover="hover"
                whileTap="tap"
              >
                {name}
                {selectedItem === name && (
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-300"
                    layoutId="underline"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  />
                )}
              </motion.div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            className="md:hidden text-white text-2xl p-2"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            {sidebarOpen ? <FaTimes /> : <FaBars />}
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {sidebarOpen && (
            <motion.div
              className="md:hidden bg-white/95 backdrop-blur-md border-t border-gray-200"
              variants={mobileMenuVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <div className="flex flex-col p-4 space-y-4">
                {navArray.map((name, index) => (
                  <motion.div
                    key={index}
                    className={`px-4 py-3 rounded-lg cursor-pointer font-medium transition-all duration-200 ${
                      selectedItem === name
                        ? "bg-blue-600 text-white"
                        : "text-gray-800 hover:bg-blue-50"
                    }`}
                    onClick={() => handleClick(name)}
                    whileHover={{ x: 5 }}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {name}
                  </motion.div>
                ))}

                {/* Contact Info in Mobile Menu */}
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  <div className="flex items-center gap-3 text-gray-600 px-4">
                    <FaMobileAlt className="text-blue-600" />
                    <span className="text-sm">+91 9423376674</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-600 px-4">
                    <MdOutlineEmail className="text-blue-600" />
                    <span className="text-sm">
                      info@sbindustrialsolution.com
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Spacer for fixed navbar */}
      <div className="h-20"></div>

      {/* Page Content */}
      <Outlet />

      {/* Inquiry dialog removed - contact now scrolls to contact section */}
    </>
  );
};

export default Navbar;
